import { useMemo, useEffect } from 'react';
import { createFileRoute, notFound } from '@tanstack/react-router';
import { useSetAtom } from 'jotai';
import { DashboardPage } from '~/components/ui/dashboard/DashboardPage';
import { canAccess } from '~/utils/rbac';
import { useEnsureDashboard } from '~/hooks/useEnsureDashboard';
import { ErrorComponent } from '~/routes/__root';
import { portfolioIdAtom } from '~/atoms/dashboardAtom';
import type { Config } from '~/api/def/endpoints/dashboardEndpoint';
import { useRequireFeatureFlag } from '~/hooks/useRequireFeatureFlag';
import { FEATURE_FLAGS } from '~/config/orgConfigConstants';

// Define which widgets are allowed on the upload page
const ALLOWED_UPLOAD_WIDGETS = [
  'allocation-table',
  'allocation-table-universe',
] as const;

/**
 * Creates widget configs for upload dashboard
 * Two allocation-table widgets stacked vertically, each taking full width
 * Second widget uses allocation-table-universe which defaults to 'universe' batch type
 */
const createUploadDashboardConfig = (withUUID = true): Config[] => {
  const widget1Id = withUUID
    ? `allocation-table-widget-${crypto.randomUUID()}`
    : 'allocation-table';
  const widget2Id = withUUID
    ? `allocation-table-universe-widget-${crypto.randomUUID()}`
    : 'allocation-table-universe';

  return [
    { id: widget1Id, w: 12, h: 16, x: 0, y: 0 },
    { id: widget2Id, w: 12, h: 16, x: 0, y: 16 },
  ];
};

function UploadComponent() {
  const { portfolioId } = Route.useParams();
  const setPortfolioId = useSetAtom(portfolioIdAtom);

  // Update portfolioId atom when route changes
  useEffect(() => {
    if (portfolioId) {
      setPortfolioId(portfolioId);
    }
  }, [portfolioId, setPortfolioId]);

  const initialUploadConfig = useMemo(
    () => createUploadDashboardConfig(true),
    [],
  );
  const uploadDefaultLayout = useMemo(
    () => createUploadDashboardConfig(false),
    [],
  );

  const { dashboardId, error } = useEnsureDashboard({
    type: 'upload',
    name: 'Upload Dashboard',
    initialConfig: initialUploadConfig,
    portfolioId,
  });

  if (error) {
    return <ErrorComponent />;
  }

  return (
    <DashboardPage
      dashboardId={dashboardId || ''}
      breadcrumbLabel="Upload"
      breadcrumbUrl={`/portfolio/${portfolioId}/upload`}
      pageTaglinePrefix="Upload"
      allowedWidgets={ALLOWED_UPLOAD_WIDGETS}
      initialLayout={uploadDefaultLayout}
      emptyStateTitle="No Upload Widgets Found"
      emptyStateMessage="This dashboard doesn't have any upload widgets yet. Add holdings widgets to get started."
      emptyStateButtonLabel="Add Widget"
    />
  );
}

const UploadRoute = () => {
  const { allowed } = useRequireFeatureFlag(FEATURE_FLAGS.PORTFOLIO_MANAGEMENT);
  if (!allowed) return null;
  return <UploadComponent />;
};

export const Route = createFileRoute(
  '/_authLayout/portfolio/$portfolioId/upload',
)({
  beforeLoad: async () => {
    if (!canAccess('dashboard')) {
      throw notFound();
    }
  },
  component: UploadRoute,
});
